import React, { useState, useEffect, useCallback } from "react";
import { Table, Button, Pagination, Spinner, Alert, Badge, Modal } from "react-bootstrap";
import ReportService from "../services/report.service";
import { toast } from "react-toastify";
import EventBus from "../common/EventBus";

const BlockedWorkstationManager = () => {
    const [workstations, setWorkstations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const pageSize = 50;

    // Confirmation Modal State
    const [showConfirm, setShowConfirm] = useState(false);
    const [selectedWS, setSelectedWS] = useState(null);

    const fetchWorkstations = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await ReportService.getBlockedWorkstations(page, pageSize);
            setWorkstations(response.data.content);
            setTotalPages(response.data.totalPages);
        } catch (err) {
            console.error("Error fetching blocked workstations:", err);
            setError("Failed to load blocked workstations.");
        } finally {
            setLoading(false);
        }
    }, [page]);

    useEffect(() => {
        fetchWorkstations();
    }, [fetchWorkstations]);

    const handleUnblockClick = (ws) => {
        setSelectedWS(ws);
        setShowConfirm(true);
    };

    const handleConfirmUnblock = async () => {
        setShowConfirm(false);
        setLoading(true);

        try {
            await ReportService.unblockWorkstation(selectedWS.id);
            toast.success(`Workstation "${selectedWS.workstationName}" unblocked successfully`);
            setWorkstations(prev => prev.filter(ws => ws.id !== selectedWS.id));
        } catch (err) {
            console.error(`Error unblocking workstation:`, err);
            EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: `Failed to unblock "${selectedWS.workstationName}".` });
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const copyTextToClipboard = async (value, label) => {
        if (!value) return;

        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(value);
            } else {
                const textArea = document.createElement("textarea");
                textArea.value = value;
                textArea.setAttribute("readonly", "");
                textArea.style.position = "fixed";
                textArea.style.left = "-9999px";
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand("copy");
                document.body.removeChild(textArea);
            }

            toast.success(`${label} copied to clipboard`);
        } catch (error) {
            console.error("Failed to copy to clipboard:", error);
            toast.error(`Failed to copy ${label}`);
        }
    };

    const renderCopyButton = (value, label) => (
        <button
            type="button"
            className="btn btn-sm border-0 bg-transparent text-secondary p-0"
            onClick={() => copyTextToClipboard(value, label)}
            disabled={!value}
            title={`Copy ${label}`}
            aria-label={`Copy ${label}`}
            style={{ boxShadow: "none", lineHeight: 1 }}
        >
            <i className="bi bi-copy"></i>
        </button>
    );

    if (error) {
        return <Alert variant="danger" className="mt-3">{error}</Alert>;
    }

    return (
        <div className="container-fluid py-4" style={{ background: 'var(--bg-gradient)', minHeight: 'calc(100vh - 76px)' }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>Blocked Workstations (Trespass)</h2>
                {loading && <Spinner animation="border" size="sm" />}
            </div>

            <Table striped bordered hover responsive className="shadow-sm">
                <thead className="table-dark">
                    <tr>
                        <th>Client Name</th>
                        <th>Workstation Name</th>
                        <th>Workstation ID</th>
                        <th>Reason</th>
                        <th>Status</th>
                        <th style={{ width: '150px' }}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {workstations.length > 0 ? (
                        workstations.map((ws) => (
                            <tr key={ws.id}>
                                <td className="align-middle">
                                    <div className="d-flex align-items-center gap-1">
                                        <span>{ws.clientName}</span>
                                        {renderCopyButton(ws.clientName, "Client Name")}
                                    </div>
                                </td>
                                <td className="align-middle fw-bold">
                                    <div className="d-flex align-items-center gap-1">
                                        <span>{ws.workstationName}</span>
                                        {renderCopyButton(ws.workstationName, "Workstation Name")}
                                    </div>
                                </td>
                                <td className="align-middle text-secondary small">
                                    {ws.id}
                                </td>
                                <td className="align-middle">
                                    {ws.blockedShopName ? (
                                        <div className="d-flex align-items-center gap-1">
                                            <span>{ws.blockedShopName}</span>
                                            {renderCopyButton(ws.blockedShopName, "Blocked Shop Name")}
                                        </div>
                                    ) : (
                                        <span className="text-muted">Unknown shop</span>
                                    )}
                                </td>
                                <td className="align-middle">
                                    <div className="d-flex flex-wrap gap-1">
                                        <Badge bg="danger">Blocked (Trespass)</Badge>
                                        {ws.disabled === 1 && <Badge bg="secondary">Disabled</Badge>}
                                        {ws.blockUpload === 1 && <Badge bg="warning" text="dark">Upload Blocked</Badge>}
                                        {ws.enableDebug === 1 && <Badge bg="info" text="dark">Debug Enabled</Badge>}
                                    </div>
                                </td>
                                <td className="align-middle text-center">
                                    <Button 
                                        variant="success" 
                                        size="sm"
                                        onClick={() => handleUnblockClick(ws)}
                                    >
                                        <i className="bi bi-unlock me-1"></i> Unblock
                                    </Button>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="6" className="text-center py-4 text-muted">
                                {loading ? "Loading..." : "No blocked workstations found."}
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>

            {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                    <Pagination>
                        <Pagination.First onClick={() => handlePageChange(0)} disabled={page === 0} />
                        <Pagination.Prev onClick={() => handlePageChange(page - 1)} disabled={page === 0} />
                        {[...Array(totalPages).keys()].map((p) => {
                            if (p < page - 2 || p > page + 2) return null;
                            return (
                                <Pagination.Item 
                                    key={p} 
                                    active={p === page}
                                    onClick={() => handlePageChange(p)}
                                >
                                    {p + 1}
                                </Pagination.Item>
                            );
                        })}
                        <Pagination.Next onClick={() => handlePageChange(page + 1)} disabled={page === totalPages - 1} />
                        <Pagination.Last onClick={() => handlePageChange(totalPages - 1)} disabled={page === totalPages - 1} />
                    </Pagination>
                </div>
            )}

            {/* Confirmation Modal */}
            <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
                <Modal.Header closeButton className="bg-success text-white">
                    <Modal.Title>Confirm Unblock</Modal.Title>
                </Modal.Header>
                <Modal.Body className="py-4">
                    <p>Are you sure you want to <strong>unblock</strong> workstation <strong>"{selectedWS?.workstationName}"</strong> for client <strong>"{selectedWS?.clientName}"</strong>?</p>
                    <p className="text-muted small mb-0">This will restore access for this workstation if it was previously blocked due to trespass.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirm(false)}>Cancel</Button>
                    <Button variant="success" onClick={handleConfirmUnblock}>
                        Confirm Unblock
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default BlockedWorkstationManager;
